import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, ChevronDown, Flag, Lock, MoreHorizontal, Pencil, Plus, SearchX, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import StatusBadge from '../StatusBadge';
import EmptyState from '../EmptyState';
import MenuButton from '../table/MenuButton';
import ConfirmDialog from '../ui/ConfirmDialog';
import FilterBar, { type FilterDef } from '../ui/FilterBar';
import type { MenuItemDef } from '../ui/Menu';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import { formatDate } from '../../utils/format';
import { MILESTONE_STEPS } from './milestoneStatusModel';
import {
  emptyMilestoneFilters, hasMilestoneFilters, isMilestoneOverdue, matchesMilestoneFilters, orderMilestoneRows, type MilestoneFilters,
} from './milestoneListModel';
import MilestoneProgressBar from './MilestoneProgressBar';
import type { Milestone } from '../../types';
import { undoableDeleteMilestone } from '../../utils/undoableDeletes';
import { offerUndoStatus, useHiddenRows } from '../../utils/undoableActions';

/**
 * P-05/E (D-39, D-52) — the project page's "אבני דרך" tab:
 *
 *   [🔍] [סטטוס▾] [אחראי▾] [מצוות▾] [☐ באיחור] [☐ הושלמו]        [+ אבן דרך]
 *   🔒 פיתוח (5)   רוני · דנה, יוסי   01/09 – 31/10   🔵 בביצוע   ▓▓▓░ 60%   [✎][⋯]
 *      ↳ מודול תשלומים (2) …
 *   ▸ בארכיון (N)
 *
 * The 4 core milestones first, in their order, then the rest; sub-milestones
 * indented under their parent. A row opens the milestone page. [✎] / [⋯] /
 * [+ אבן דרך] show only with the permission (D-53, G).
 */
export default function ProjectMilestonesTab({ milestones, projectId, canCreate, canEdit, canDelete, onCreate, onCreateSub, onEdit }: {
  milestones: Milestone[];
  projectId: string;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onCreate: () => void;
  /** P-56 (D-231 §4): "+ תת-אבן דרך" on a main milestone's row (the parent pre-set). */
  onCreateSub?: (parent: Milestone) => void;
  onEdit: (m: Milestone) => void;
}) {
  const { t } = useTranslation();
  // P-06/G (D-58) — the tab's filters start from the URL and are written back
  // (replace), so "← חזרה" to the project restores them.
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<MilestoneFilters>(() => ({
    ...emptyMilestoneFilters(),
    search: searchParams.get('mq') ?? '',
    status: searchParams.get('mstatus') ?? '',
    responsible: searchParams.get('mresp') ?? '',
    assignee: searchParams.get('masg') ?? '',
    overdue: searchParams.get('mlate') === '1',
    completed: searchParams.get('mdone') === '1',
  }));
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const put = (k: string, v: string) => { if (v) next.set(k, v); else next.delete(k); };
      put('mq', filters.search);
      put('mstatus', filters.status);
      put('mresp', filters.responsible);
      put('masg', filters.assignee);
      put('mlate', filters.overdue ? '1' : '');
      put('mdone', filters.completed ? '1' : '');
      return next;
    }, { replace: true });
  }, [filters, setSearchParams]);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const setF = <K extends keyof MilestoneFilters>(k: K, v: MilestoneFilters[K]) => setFilters((f) => ({ ...f, [k]: v }));
  const filtering = hasMilestoneFilters(filters);

  const people = useMemo(() => {
    const responsible = new Map<string, string>();
    const assignees = new Map<string, string>();
    for (const m of milestones) {
      if (m.responsible_user_id) responsible.set(m.responsible_user_id, m.responsible_name || m.responsible_user_id);
      for (const a of m.assignees ?? []) assignees.set(a.user_id, a.name);
    }
    const opts = (map: Map<string, string>) => [...map.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'he'));
    return { responsible: opts(responsible), assignees: opts(assignees) };
  }, [milestones]);

  const rows = useMemo(
    () => orderMilestoneRows(milestones, (m) => matchesMilestoneFilters(m, filters)),
    [milestones, filters],
  );

  const filterDefs: FilterDef[] = [
    { key: 'status', label: t('project.filterByStatus'), value: filters.status, allLabel: t('project.allStatuses'), options: MILESTONE_STEPS.map((s) => ({ value: s, label: t(`milestoneStatus.${s}`) })), onChange: (v) => setF('status', v) },
    { key: 'responsible', kind: 'search-select', label: t('milestoneList.responsible'), value: filters.responsible, allLabel: t('milestoneList.allResponsible'), options: people.responsible, onChange: (v) => setF('responsible', v) },
    { key: 'assignee', kind: 'search-select', label: t('milestoneList.assignee'), value: filters.assignee, allLabel: t('milestoneList.allAssignees'), options: people.assignees, onChange: (v) => setF('assignee', v) },
  ];
  const reset = () => setFilters(emptyMilestoneFilters());
  const shown = rows.live.length + rows.archived.length;

  return (
    <section aria-labelledby="project-ms-title" className="space-y-3 pb-fab-list">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="project-ms-title" className="text-base font-semibold text-text">{t('project.milestones')}</h2>
        {canCreate && (
          <button type="button" onClick={onCreate} className="btn btn-secondary">
            <Plus size={16} aria-hidden="true" /> {t('projectPage.addMilestone')}
          </button>
        )}
      </div>
      <FilterBar
        search={filters.search}
        onSearchChange={(v) => setF('search', v)}
        searchPlaceholder={t('milestoneList.search')}
        filters={filterDefs}
        inlineFilters={0}
        onClearFilters={reset}
        onClearAll={reset}
        summary={filtering ? t('project.showingResults', { shown, total: milestones.length }) : undefined}
        end={(
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex min-h-8 items-center gap-1.5 text-sm text-text-2">
              <input type="checkbox" checked={filters.overdue} onChange={(e) => setF('overdue', e.target.checked)} />
              {t('milestoneList.overdueOnly')}
            </label>
            <label className="inline-flex min-h-8 items-center gap-1.5 text-sm text-text-2">
              <input type="checkbox" checked={filters.completed} onChange={(e) => setF('completed', e.target.checked)} />
              {t('milestoneList.completedOnly')}
            </label>
          </div>
        )}
      />

      <div className="rounded-xl border border-border bg-surface">
        {shown === 0 ? (
          milestones.length === 0
            ? <EmptyState icon={Flag} title={t('project.noMilestones')} />
            : <EmptyState icon={SearchX} title={t('milestoneList.noMatch')} action={<button type="button" className="btn btn-secondary" onClick={reset}>{t('project.clearFiltersAction')}</button>} />
        ) : (
          <ul className="divide-y divide-border">
            {rows.live.map(({ milestone, depth }) => (
              <MilestoneRow key={milestone.id} milestone={milestone} depth={depth} projectId={projectId} canEdit={canEdit} canDelete={canDelete} onEdit={onEdit} onCreateSub={canCreate ? onCreateSub : undefined} />
            ))}
            {rows.archived.length > 0 && (
              <li>
                <button
                  type="button"
                  onClick={() => setArchivedOpen((o) => !o)}
                  aria-expanded={archivedOpen}
                  aria-controls="ms-archived"
                  className="flex w-full items-center gap-2 px-4 sm:px-5 py-3 text-sm font-medium text-text-2 hover:bg-surface-subtle"
                >
                  <ChevronDown size={16} className={`flex-shrink-0 transition-transform duration-150 ${archivedOpen ? '' : 'ltr:-rotate-90 rtl:rotate-90'}`} aria-hidden="true" />
                  {t('project.archivedMilestones', { count: rows.archived.length })}
                </button>
                {archivedOpen && (
                  <ul id="ms-archived" className="divide-y divide-border border-t border-border">
                    {rows.archived.map(({ milestone, depth }) => (
                      <MilestoneRow key={milestone.id} milestone={milestone} depth={depth} projectId={projectId} canEdit={canEdit} canDelete={canDelete} onEdit={onEdit} onCreateSub={canCreate ? onCreateSub : undefined} />
                    ))}
                  </ul>
                )}
              </li>
            )}
          </ul>
        )}
      </div>
    </section>
  );
}

/** One row: name (task count), responsible · assignees, dates, status badge, progress, [✎][⋯]. */
function MilestoneRow({ milestone: m, depth, projectId, canEdit, canDelete, onEdit, onCreateSub }: {
  milestone: Milestone;
  depth: 0 | 1;
  projectId: string;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (m: Milestone) => void;
  onCreateSub?: (parent: Milestone) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const href = `/milestones/${m.id}?from=/projects/${projectId}`;
  const isCore = !!m.is_core;
  const archived = !!m.archived_at;
  const overdue = isMilestoneOverdue(m);
  const taskCount = m.metrics?.task_count ?? 0;
  const assignees = (m.assignees ?? []).map((a) => a.name).filter(Boolean);
  const dash = <span className="text-text-3">—</span>;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    queryClient.invalidateQueries({ queryKey: ['milestones', projectId] });
  };
  // P-27 (D-166 s.11): a delete leaves the server after 6 s ("בטל" cancels it, the row is
  // hidden at once); an archive happens at once and "בטל" restores it through the server.
  const hiddenNow = useHiddenRows((s) => s.hidden[`milestone:${m.id}`] === true);
  const requestDelete = () => {
    setConfirmingDelete(false);
    undoableDeleteMilestone({ qc: queryClient, id: m.id });
  };
  const action = useMutation({
    mutationFn: async (kind: 'archive' | 'restore'): Promise<void> => {
      if (kind === 'archive') await api.archiveMilestone(m.id);
      else await api.restoreMilestone(m.id);
    },
    onSuccess: (_res, kind) => {
      refresh();
      setConfirmingDelete(false);
      if (kind === 'archive') {
        offerUndoStatus({ message: t('project.milestoneArchived'), revert: () => api.restoreMilestone(m.id), onReverted: refresh });
      } else toast('success', t('project.milestoneRestored'));
    },
    onError: (err) => {
      setConfirmingDelete(false);
      toast('error', apiErrorMessage(err, t('project.actionFailed')));
    },
  });
  // P-02a (D-22, D-25): a core milestone is never deleted or archived; an empty one
  // (no work, no sub-milestones) is deleted; any other is archived / restored.
  const menuItems: MenuItemDef[] = !canDelete || isCore ? [] : archived
    ? [{ key: 'restore', label: t('project.restoreMilestoneAction'), icon: <ArchiveRestore size={16} strokeWidth={1.75} />, onSelect: () => action.mutate('restore') }]
    : m.has_work || m.has_sub_milestones
      ? [{ key: 'archive', label: t('project.archiveMilestoneAction'), icon: <Archive size={16} strokeWidth={1.75} />, onSelect: () => action.mutate('archive') }]
      : [{ key: 'delete', label: t('project.deleteMilestoneAction'), icon: <Trash2 size={16} strokeWidth={1.75} />, danger: true, onSelect: () => setConfirmingDelete(true) }];

  if (hiddenNow) return null;
  return (
    <li
      className="dt-row relative isolate flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3 hover:bg-surface-subtle sm:px-5"
      data-testid="milestone-row"
    >
      <div className={`min-w-0 flex-1 basis-56 ${depth === 1 ? 'ps-6' : ''}`}>
        <p className="flex items-center gap-1.5 text-sm font-medium text-text">
          {depth === 1 && <span className="text-text-3" aria-hidden="true">↳</span>}
          {isCore && (
            <span className="text-text-3" title={t('project.coreMilestone')}>
              <Lock size={12} className="inline align-[-1px]" aria-hidden="true" />
              <span className="sr-only">{t('project.coreMilestone')}</span>
            </span>
          )}
          <Link to={href} className="dt-link truncate hover:text-accent-text hover:underline underline-offset-2"><bdi>{m.name}</bdi></Link>
          <span className="text-xs font-normal tabular-nums text-text-2">({taskCount})</span>
        </p>
        <p className="mt-0.5 truncate text-xs text-text-2">
          {m.responsible_name || dash}
          {assignees.length > 0 && <> · {assignees.join(', ')}</>}
        </p>
      </div>
      <span className={`whitespace-nowrap text-xs tabular-nums ${overdue ? 'font-medium text-danger-700' : 'text-text-2'}`}>
        {m.start_date || m.due_date ? <>{m.start_date ? formatDate(m.start_date) : '—'} – {m.due_date ? formatDate(m.due_date) : '—'}</> : dash}
      </span>
      <StatusBadge status={m.status as any} />
      <MilestoneProgressBar value={m.metrics?.progress ?? 0} label={m.name} className="w-28" />
      <div className="dt-above flex items-center gap-1">
        {/* P-56 (D-231 §4) — the same action as the milestone page: never on a sub-milestone, never on an archived one. */}
        {onCreateSub && depth === 0 && !archived && (
          <button
            type="button"
            title={t('project.addSubMilestoneAction')}
            aria-label={t('project.addSubMilestoneNamed', { name: m.name })}
            className="btn btn-ghost btn-sm text-text-2 hover:text-text"
            onClick={() => onCreateSub(m)}
            data-testid="add-sub-milestone"
          >
            <Plus size={14} aria-hidden="true" /> {t('project.addSubMilestoneAction')}
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            title={t('project.editMilestoneAction')}
            aria-label={t('project.editMilestoneNamed', { name: m.name })}
            className="btn-icon text-text-3 hover:bg-surface-subtle hover:text-text"
            onClick={() => onEdit(m)}
          >
            <Pencil size={14} aria-hidden="true" />
          </button>
        )}
        {menuItems.length > 0 && (
          <MenuButton items={menuItems} label={t('project.milestoneActions', { name: m.name })} triggerClassName="btn-icon text-text-3 hover:bg-surface-subtle hover:text-text">
            <MoreHorizontal size={14} aria-hidden="true" />
          </MenuButton>
        )}
      </div>
      <ConfirmDialog
        open={confirmingDelete}
        title={t('project.deleteMilestoneTitle')}
        description={t('project.deleteMilestoneConfirm', { name: m.name })}
        confirmLabel={t('project.deleteMilestoneAction')}
        loading={action.isPending}
        onConfirm={requestDelete}
        onCancel={() => setConfirmingDelete(false)}
        danger
      />
    </li>
  );
}
